import { useId, useRef, type ReactNode } from "react";
import { TriangleAlert } from "lucide-react";
import { Button } from "../../atoms/index.js";
import { Modal } from "../Modal/Modal.js";

export interface ConfirmDialogProps {
  readonly open: boolean;
  /** Asks the question, e.g. "Unlink SOLVE-12?" — a question, not a label. */
  readonly title: string;
  /** What will happen, in one sentence. Say what is NOT lost where that is the
   *  part people fear; a confirmation that only warns teaches nothing. */
  readonly children: ReactNode;
  /** The verb, matching the button that opened this — "Unlink", not "OK". */
  readonly confirmLabel: string;
  /** The action's severity — the same subset ActionMenuItem uses. `critical`
   *  (default) is a destructive confirm with the warning chip; `neutral` is a
   *  primary confirm with no chip. It picks the Button variant only, never a
   *  Button tone. */
  readonly tone?: "neutral" | "critical";
  /** The confirm is in flight: the confirm Button shows its spinner and
   *  aria-busy, and Cancel, Esc, outside press and the header Close are
   *  blocked by the same guard as `cancelDisabled`. */
  readonly loading?: boolean;
  /** The dismiss text, e.g. "Keep editing". Defaults to "Cancel". */
  readonly cancelLabel?: string;
  readonly confirmDisabled?: boolean;
  readonly cancelDisabled?: boolean;
  readonly onClose: () => void;
  readonly onConfirm: () => void;
}

/**
 * The shared "are you sure" for a consequential act — destructive by default,
 * `tone="neutral"` for one that loses nothing.
 *
 * Cancel takes initial focus, never Confirm, in both tones: a dialog that
 * opens with the confirm button focused is dismissed with the same Enter
 * keypress that opened it, which is worse than no dialog because it teaches
 * people the gesture is safe.
 *
 * It stacks. The overlay engine's layer stack routes Esc and outside-press to
 * the TOPMOST layer only (`isTopLayer`), so this is safe to raise from inside
 * another modal — which the linked-work list needs, being rendered inside the
 * Related work dialog.
 *
 * Generalised from `AccessActionConfirmationDialog`, which now renders through
 * this with "Remove access for X" wired in, reached through the roster's
 * `confirmation` flag. There were already four hand-rolled copies of this
 * Modal in the repo (`DeleteHoshinPlanModal`, `DeleteSolveModal`,
 * `DeleteVsmModal`, and that one), so the next confirmation should reach for
 * this rather than a fifth.
 */
export function ConfirmDialog({
  open,
  title,
  children,
  confirmLabel,
  tone = "critical",
  loading = false,
  cancelLabel = "Cancel",
  confirmDisabled = false,
  cancelDisabled = false,
  onClose,
  onConfirm,
}: ConfirmDialogProps): JSX.Element {
  const descriptionId = useId();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const dismissBlocked = cancelDisabled || loading;
  const critical = tone !== "neutral";
  const handleClose = () => {
    if (!dismissBlocked) onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={title}
      describedBy={descriptionId}
      size="sm"
      initialFocusRef={cancelRef}
      footer={
        <>
          <Button
            ref={cancelRef}
            variant="ghost"
            onClick={handleClose}
            disabled={dismissBlocked}
          >
            {cancelLabel}
          </Button>
          <Button
            variant={critical ? "destructive" : "primary"}
            disabled={confirmDisabled}
            loading={loading}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="flex items-start gap-3">
        {critical && (
          <span className="rounded-full bg-pb-red/10 p-2 text-pb-red-text">
            <TriangleAlert aria-hidden className="h-5 w-5" />
          </span>
        )}
        <div id={descriptionId} className="pt-1 text-sm text-pb-text-secondary">
          {children}
        </div>
      </div>
    </Modal>
  );
}
